import { Directive, OnDestroy, inject } from '@angular/core';

import { SharedLanguageService } from '@language';
import { BehaviorSubject, NEVER, Observable, Subject } from 'rxjs';
import { NotificationType } from '../../../elements/components/notification/entities/notification-types.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { FocusManagerService } from '../services/focus-manager.service';

@Directive()
export abstract class BaseContainer implements OnDestroy {
    protected readonly componentId: string = `${this.constructor.name}-${Math.random().toString(36).slice(0, 9)}`;

    readonly notificationType: typeof NotificationType = NotificationType;

    readonly destroy$: Subject<void> = new Subject<void>();
    readonly loading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);
    readonly error$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly focusManager: FocusManagerService = inject(FocusManagerService);
    protected readonly translateService: SharedLanguageService = inject(SharedLanguageService);

    ngOnDestroy(): void {
        this.loading$.complete();
        this.error$.complete();

        this.destroy$.next();
        this.destroy$.complete();
    }

    setLoading(value: boolean): void {
        this.loading$.next(value);
    }

    setError(value: boolean): void {
        this.error$.next(value);
    }

    protected loadingFailed(error?: any): Observable<never> {
        if (error) {
            console.error(error);
        }
        this.setError(true);
        this.setLoading(false);
        return NEVER;
    }

    protected resetLoadingFailed(): Observable<never> {
        this.setError(false);
        this.setLoading(false);
        return NEVER;
    }

    protected afterFetching(): void {
        this.setLoading(false);
    }

    protected activate() {
        this.focusManager.setActive(this.componentId);
    }

    protected deactivate() {
        this.focusManager.clearActive();
    }

    protected isActiveComponent(): boolean {
        return this.focusManager.currentActive === this.componentId;
    }
}
